import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { ChartContainer, CHART_COLORS, useAdminContext } from './shared';
import './AdminPages.css';

interface TrendData {
  version: string;
  surveyId: string;
  responseCount: number;
  firstResponse: string;
  lastResponse: string;
}

interface TimelineData {
  date: string;
  surveyId: string;
  responses: number;
}

interface DailyData {
  date: string;
  count: number;
}

const SurveyTrends: React.FC = () => {
  const { token } = useAuth();
  const { respondentType } = useAdminContext();
  const [data, setData] = useState<TrendData[]>([]);
  const [timelineData, setTimelineData] = useState<TimelineData[]>([]);
  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;

      setIsLoading(true);
      setError(null);

      try {
        const headers = { Authorization: `Bearer ${token}` };

        const roleParam = `role=${respondentType}`;

        // Fetch all data in parallel
        const [trendsRes, timelineRes, dailyRes] = await Promise.all([
          fetch(`/api/admin/stats/trends?${roleParam}`, { headers }),
          fetch(`/api/admin/stats/timeline?period=day&${roleParam}`, { headers }),
          fetch(`/api/admin/stats/daily?${roleParam}`, { headers }),
        ]);

        if (!trendsRes.ok) throw new Error('Failed to fetch trends');

        const trendsData = await trendsRes.json();
        setData(trendsData.data);

        if (timelineRes.ok) {
          const timelineJson = await timelineRes.json();
          setTimelineData(timelineJson.data);
        }

        if (dailyRes.ok) {
          const dailyJson = await dailyRes.json();
          setDailyData(dailyJson.data);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, respondentType]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Process timeline data for multi-series chart
  const processTimelineData = () => {
    const dateMap: Record<string, Record<string, number>> = {};
    const surveyIds = new Set<string>();

    timelineData.forEach((item) => {
      const dateStr = new Date(item.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });

      if (!dateMap[dateStr]) {
        dateMap[dateStr] = {};
      }
      dateMap[dateStr][item.surveyId] = (dateMap[dateStr][item.surveyId] || 0) + item.responses;
      surveyIds.add(item.surveyId);
    });

    const sortedDates = Object.keys(dateMap).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    return {
      data: sortedDates.map((date) => ({
        date,
        ...dateMap[date],
      })),
      surveyIds: Array.from(surveyIds),
    };
  };

  // Process daily data for total responses chart
  const processDailyData = () => {
    return dailyData
      .slice(-30) // Last 30 days
      .map((item) => ({
        date: new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        responses: item.count,
      }));
  };

  const { data: chartData, surveyIds } = processTimelineData();
  const dailyChartData = processDailyData();

  // Format survey ID for display
  const formatSurveyId = (id: string) => {
    return id
      .replace('student-', '')
      .replace('staff-', '')
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  return (
    <div className="admin-page trends-page">
      <div className="page-header">
        <div>
          <h2>Survey Trends</h2>
          <p className="page-subtitle">Response trends over time and across survey versions</p>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading data...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>Error: {error}</p>
        </div>
      ) : (
        <>
          {/* Daily Responses Chart */}
          <ChartContainer
            title="Daily Response Volume"
            subtitle="Responses per day over the last 30 days"
            className="trends-chart"
            isEmpty={dailyChartData.length === 0}
            emptyMessage="No daily response data available"
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#6d6d70' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6d6d70' }}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e5e5ea',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="responses"
                  stroke={CHART_COLORS.primary}
                  strokeWidth={2}
                  dot={{ fill: CHART_COLORS.primary, strokeWidth: 0, r: 3 }}
                  activeDot={{ r: 5, fill: CHART_COLORS.primary }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>

          {/* Responses by Survey Type Chart */}
          {surveyIds.length > 0 && (
            <ChartContainer
              title="Responses by Survey Type"
              subtitle="Daily breakdown by survey type"
              className="trends-chart"
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#6d6d70' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#6d6d70' }}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e5e5ea',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend
                    formatter={(value) => (
                      <span style={{ color: '#1d1d1f', fontSize: '0.85rem' }}>
                        {formatSurveyId(value)}
                      </span>
                    )}
                  />
                  {surveyIds.map((surveyId, index) => (
                    <Line
                      key={surveyId}
                      type="monotone"
                      dataKey={surveyId}
                      name={surveyId}
                      stroke={CHART_COLORS.series[index % CHART_COLORS.series.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          )}

          {/* Version Table */}
          <div className="trends-table-section">
            <h3>Survey Versions</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Survey Version</th>
                    <th>Survey Type</th>
                    <th>Responses</th>
                    <th>First Response</th>
                    <th>Last Response</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={index}>
                      <td><strong>{item.version || '-'}</strong></td>
                      <td><code>{item.surveyId}</code></td>
                      <td>{item.responseCount.toLocaleString()}</td>
                      <td>{formatDate(item.firstResponse)}</td>
                      <td>{formatDate(item.lastResponse)}</td>
                    </tr>
                  ))}
                  {data.length === 0 && (
                    <tr>
                      <td colSpan={5} className="empty-state">
                        No version data available yet. Data will appear once surveys have been completed.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SurveyTrends;
